<template>
  <section class="login">
    <div class="login__inner">
      <WhiteBlock class="login__white-block">
        <Heading class="login__header" :tag="HEADINGS.h4">
          {{ $t('auth.loginForm.header') }}
        </Heading>
        <div v-if="$route.params.type === AUTH_TYPES.login" class="login__form-block">
          <div
            class="login__subheader"
            v-html="resolveLineBreakOfLastWord($t('auth.loginForm.subheader'))"
          />
          <Form
            :getFieldComponents="() => $refs.loginFields"
            :buttonText="$t('auth.login')"
            :handleSubmit="handleLoginSubmit"
            :isRedirecting="isRedirecting"
          >
            <component
              :is="field.component"
              v-for="field in fieldsArray[AUTH_TYPES.login]"
              ref="loginFields"
              :key="field.props.id"
              :model.sync="formData[AUTH_TYPES.login][field.props.id]"
              v-bind="field.props"
            />
            <div class="login__forgot-link-wrap">
              <nuxt-link
                class="login__forgot-link"
                :to="getPath(AUTH_TYPES.passwordForgotten)"
                @click.native="trackLinkClick(AUTH_TYPES.passwordForgotten)"
              >
                {{ $t('auth.passwordForgotten') }}
              </nuxt-link>
            </div>
          </Form>
          <div class="login__navigation-cta-wrap">
            {{ $t('auth.loginForm.youAreNew') }}
            <Link
              :to="getPath(AUTH_TYPES.signup)"
              :isNoWrap="true"
              @click="trackLinkClick(AUTH_TYPES.signup)"
              >{{ $t('auth.signup') }}</Link
            >
          </div>
        </div>
        <div v-if="$route.params.type === AUTH_TYPES.signup" class="login__form-block">
          <div
            class="login__subheader"
            v-html="resolveLineBreakOfLastWord($t('auth.signupForm.subheader'))"
          />
          <Form
            :getFieldComponents="() => $refs.signupFields"
            :buttonText="$t('auth.signup')"
            :handleSubmit="handleSignupSubmit"
          >
            <component
              :is="field.component"
              v-for="field in fieldsArray[AUTH_TYPES.signup]"
              ref="signupFields"
              :key="field.props.id"
              :model.sync="formData[AUTH_TYPES.signup][field.props.id]"
              v-bind="field.props"
              v-on="getValidateAlsoListeners(field, 'signupFields')"
            />
          </Form>
          <div class="login__navigation-cta-wrap">
            {{ $t('auth.signupForm.alreadyHaveAccount') }}
            <Link
              :to="getPath(AUTH_TYPES.login)"
              :isNoWrap="true"
              @click="trackLinkClick(AUTH_TYPES.login)"
              >{{ $t('auth.login') }}</Link
            >
          </div>
        </div>
        <div v-if="$route.params.type === AUTH_TYPES.passwordForgotten" class="login__form-block">
          <div
            class="login__subheader"
            v-html="resolveLineBreakOfLastWord($t('auth.passwordForgottenForm.subheader'))"
          />
          <Form
            :getFieldComponents="() => $refs.passwordForgottenFields"
            :buttonText="$t('auth.sendEmail')"
            :handleSubmit="handlePasswordForgottenSubmit"
          >
            <component
              :is="field.component"
              v-for="field in fieldsArray[AUTH_TYPES.passwordForgotten]"
              ref="passwordForgottenFields"
              :key="field.props.id"
              :model.sync="formData[AUTH_TYPES.passwordForgotten][field.props.id]"
              v-bind="field.props"
            />
          </Form>
          <div class="login__navigation-cta-wrap">
            {{ $t('auth.noMoreHelpNeeded') }}
            <Link
              :to="getPath(AUTH_TYPES.login)"
              :isNoWrap="true"
              @click="trackLinkClick(AUTH_TYPES.login)"
              >{{ $t('auth.login') }}</Link
            >
          </div>
        </div>
        <div v-if="$route.params.type === AUTH_TYPES.resetPassword" class="login__form-block">
          <div
            class="login__subheader"
            v-html="resolveLineBreakOfLastWord($t('auth.resetPasswordForm.subheader'))"
          />
          <Form
            :getFieldComponents="() => $refs.resetPasswordFields"
            :buttonText="$t('form.save')"
            :handleSubmit="handleResetPasswordSubmit"
          >
            <component
              :is="field.component"
              v-for="field in fieldsArray[AUTH_TYPES.resetPassword]"
              ref="resetPasswordFields"
              :key="field.props.id"
              :model.sync="formData[AUTH_TYPES.resetPassword][field.props.id]"
              v-bind="field.props"
              v-on="getValidateAlsoListeners(field, 'resetPasswordFields')"
            />
          </Form>
          <div class="login__navigation-cta-wrap">
            {{ $t('auth.noMoreHelpNeeded') }}
            <Link :to="getPath(AUTH_TYPES.login)" :isNoWrap="true">{{ $t('auth.goBack') }}</Link>
          </div>
        </div>
      </WhiteBlock>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { resetPasswordTokenUrlParam } from 'acces-impot-settings-user'
import i18nAuthentication from '@/locales/authentication.json'
import { ROUTE_NAMES, AUTH_TYPES, AUTH_REFERER_KEY } from '@/constants/route-names'
import { track, TRACK_CATEGORIES, TRACK_CONTEXTS } from '@/services/tracking'
import { resolveLineBreakOfLastWord } from '@/helpers/string'
import { waitFor } from '@/helpers/time'
import { loadForAtLeast } from '@/helpers/request'
import { mergeLocaleMessages } from '@/helpers/i18n'
import mountedMixin from '@/mixins/mounted'
import Heading, { HEADINGS } from '@/components/Heading/Heading.vue'
import WhiteBlock from '@/components/WhiteBlock/WhiteBlock.vue'
import Form from '@/components/Form/Form.vue'
import Link from '@/components/Link/Link.vue'
import { BUTTON_STYLES } from '@/components/Form/Button.vue'
import { getFields } from './login-settings'

export default {
  name: 'Login',
  components: {
    Form,
    Heading,
    Link,
    WhiteBlock,
  },
  mixins: [mountedMixin],

  data() {
    return {
      AUTH_TYPES,
      BUTTON_STYLES,
      fieldsArray: {
        [AUTH_TYPES.login]: getFields.call(this, AUTH_TYPES.login),
        [AUTH_TYPES.signup]: getFields.call(this, AUTH_TYPES.signup),
        [AUTH_TYPES.passwordForgotten]: getFields.call(this, AUTH_TYPES.passwordForgotten),
        [AUTH_TYPES.resetPassword]: getFields.call(this, AUTH_TYPES.resetPassword),
      },
      formData: {
        [AUTH_TYPES.login]: {
          email: '',
          password: '',
        },
        [AUTH_TYPES.signup]: {
          email: '',
          password: '',
          passwordConfirmation: '',
        },
        [AUTH_TYPES.passwordForgotten]: {
          email: '',
        },
        [AUTH_TYPES.resetPassword]: {
          password: '',
          passwordConfirmation: '',
        },
      },
      HEADINGS,
      isRedirecting: false,
      resolveLineBreakOfLastWord,
    }
  },

  computed: {
    ...mapState('account', ['isCurrentUserFetched']),

    ...mapGetters({
      isAdmin: 'account/isAdmin',
    }),
  },

  beforeCreate() {
    mergeLocaleMessages(this.$i18n, i18nAuthentication)
  },

  mounted() {
    waitFor(this, () => this.isCurrentUserFetched).then(() => this.showPage())
  },

  methods: {
    ...mapActions({
      showPage: 'page/show',
      login: 'account/login',
      signup: 'account/signup',
      sendPasswordForgottenEmail: 'account/sendPasswordForgottenEmail',
      resetPassword: 'account/resetPassword',
    }),

    getPath(formType) {
      return this.localeRoute({
        name: ROUTE_NAMES.auth,
        params: { type: formType },
      })
    },

    validateFieldById(fields, id) {
      if (fields) fields.find(field => field.id === id)?.validateIfNeeded?.()
    },

    updateIsRedirecting(userResponse) {
      if (userResponse && !userResponse.error) return (this.isRedirecting = true)
    },

    redirectToAccountOrAdmin() {
      let authReferer = process.browser && localStorage.getItem(AUTH_REFERER_KEY)
      let routeSection = 'account'
      let routeName = ROUTE_NAMES.account.index

      if (this.isAdmin) {
        routeSection = 'admin'
        routeName = ROUTE_NAMES.admin.index
      }
      if (!new RegExp(`^/[^/]+/${routeSection}/`).test(authReferer)) authReferer = ''

      this.$router.push(authReferer || this.localeRoute({ name: routeName }))
    },

    redirectToAccountNotConfirmed() {
      this.$router.push(this.localeRoute({ name: ROUTE_NAMES.accountNotConfirmed }))
    },

    getValidateAlsoListeners(field, fieldsRef) {
      return field.validateAlso
        ? {
            'update:model': () => this.validateFieldById(this.$refs[fieldsRef], field.validateAlso),
          }
        : {}
    },

    async handleLoginSubmit() {
      const response = await loadForAtLeast(this.login(this.formData[AUTH_TYPES.login]))
      if (this.updateIsRedirecting(response)) this.redirectToAccountOrAdmin()
      return response
    },

    async handleSignupSubmit() {
      const { email, password } = this.formData[AUTH_TYPES.signup]
      const response = await loadForAtLeast(this.signup({ email, password }))
      if (this.updateIsRedirecting(response)) this.redirectToAccountNotConfirmed()
      return response
    },

    async handlePasswordForgottenSubmit() {
      const response = await this.sendPasswordForgottenEmail(
        this.formData[AUTH_TYPES.passwordForgotten]
      )
      this.updateIsRedirecting(response)
      return response
    },

    async handleResetPasswordSubmit() {
      const response = await loadForAtLeast(
        this.resetPassword({
          formData: this.formData[AUTH_TYPES.resetPassword],
          token: this.$route.query[resetPasswordTokenUrlParam],
        })
      )
      if (this.updateIsRedirecting(response)) this.redirectToAccountOrAdmin()
      return response
    },

    trackLinkClick(linkId) {
      track(TRACK_CATEGORIES.navigation, TRACK_CONTEXTS.authenticationPage, { linkId })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Login';
</style>
